import { NextPage } from 'next';
import {
  getServerLocale,
  initServerTranslation,
} from '@bts-web/utils-lokalise';
import { TFunction } from 'i18next';
import { getAppConfig } from '@bts-web/core-app-config';
import { getAssetById } from '../../common';
import { AssetDetailTradeWrapper, GenericErrorMessage } from '../../layouts';
import { AssetDetailsControllerWithSuspense } from '../AssetDetailsControllerWithSuspense';
import { AssetPageInitiateTradeBox } from '../../trade';
import { getTradeTranslations } from '../../trade/utils';
import { getSwapAssetsDataAndExchangeRate } from '../../common/gqlActions/getSwapAssetsDataAndExchangeRate';

export interface AssetDetailsPageProps {
  params: Promise<{
    assetId: string;
    operation?: string;
  }>;
  searchParams: Promise<{
    swapWith?: string;
  }>;
}

const AssetDetailByIdIndex: NextPage<AssetDetailsPageProps> = async ({
  params,
  searchParams,
}) => {
  const { assetId } = await params;

  const { swapWith } = await searchParams;

  const { t } = await initServerTranslation();

  const tradeTranslations = getTradeTranslations(t as TFunction);

  const locale = await getServerLocale();

  const { currency } = getAppConfig();

  const { data: assetsResponse } = swapWith
    ? await getSwapAssetsDataAndExchangeRate({
        sourceAssetId: assetId,
        targetAssetId: swapWith,
      })
    : await getAssetById(assetId);

  if (!assetsResponse) {
    console.error('[AssetDetailByIdIndex] Failed to fetch asset data', {
      assetId,
      swapWith,
    });

    return <GenericErrorMessage />;
  }

  const sourceAsset = swapWith
    ? (
        assetsResponse as NonNullable<
          Awaited<ReturnType<typeof getSwapAssetsDataAndExchangeRate>>['data']
        >
      ).asset
    : (assetsResponse as Awaited<ReturnType<typeof getAssetById>>['data']);

  const targetAsset = swapWith
    ? (
        assetsResponse as NonNullable<
          Awaited<ReturnType<typeof getSwapAssetsDataAndExchangeRate>>['data']
        >
      ).targetAsset
    : undefined;

  if (!sourceAsset || (swapWith && !targetAsset)) {
    return <GenericErrorMessage />;
  }

  const initialExchangeRate = (
    assetsResponse as NonNullable<
      Awaited<ReturnType<typeof getSwapAssetsDataAndExchangeRate>>['data']
    >
  ).exchangeRate;

  return (
    <AssetDetailTradeWrapper
      AssetDetailsContent={
        <AssetDetailsControllerWithSuspense assetId={assetId} />
      }
      hideMobile="tradeElement"
      TradeStateContent={
        <AssetPageInitiateTradeBox
          assetInitialData={sourceAsset}
          currency={currency}
          currentLocale={locale}
          tradeTranslations={tradeTranslations}
          {...(swapWith && targetAsset
            ? {
                targetAssetInitialData: targetAsset,
                initialExchangeRate: initialExchangeRate,
              }
            : {})}
        />
      }
    />
  );
};

export { AssetDetailByIdIndex };
